import { useState } from 'react';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { StepName, usePublicationId } from '../../components/shared';
import {
  GqlCreatePropertyListingPublicationMutation,
  GqlCreatePropertyListingPublicationMutationVariables,
  GqlCreatePropertyListingPublicationUploadUrlMutation,
  GqlCreatePropertyListingPublicationUploadUrlMutationVariables,
  GqlGetPublicationBasicInformationQuery,
  GqlGetPublicationBasicInformationQueryVariables,
  GqlGetPublicationFeaturesQuery,
  GqlGetPublicationImagesQuery,
  GqlGetPublicationImagesQueryVariables,
  GqlGetPublicationLocationQuery,
  GqlGetPublicationLocationQueryVariables,
  GqlGetPublicationPreQualificationQuery,
  GqlGetPublicationPreQualificationQueryVariables,
  GqlGetPublicationPricesQuery,
  GqlGetPublicationPricesQueryVariables,
  GqlGqlError,
  GqlListUserPublicationsQuery,
  GqlListUserPublicationsQueryVariables,
  GqlPropertyListingPublicationAttributeInput,
  GqlRemovePropertyListingPublicationAttributeMutation,
  GqlRemovePropertyListingPublicationAttributeMutationVariables,
  GqlTagResourceMutation,
  GqlTagResourceMutationVariables,
  GqlUpdatePublicationAttributesMutation,
  GqlUpdatePublicationAttributesMutationVariables,
  PropertyListingPublicationAttributeInputType,
} from '@shared/infrastructure/gql/core/codegen/generated-schema';
import { AptunoApolloLink, useLoggedInUser } from '@contexts/Shared';
import { schema as getBasicInformationSchema } from '../schemas/get-publication-basic-information.core-gql';
import { schema as getLocationSchema } from '../schemas/get-publication-location.core-gql';
import { schema as getFeaturesSchema } from '../schemas/get-publication-features.core-gql';
import { schema as getPreQualificationSchema } from '../schemas/get-publication-pre_qualification.core-gql';
import { schema as getPricesSchema } from '../schemas/get-publication-prices.core-gql';
import { schema as updatePublicationAttributesSchema } from '../schemas/update-publication-attributes.core-gql';
import { schema as getPublicationImagesSchema } from '../schemas/get-publication-media-images.core-gql';
import { schema as createUploadUrlSchema } from '../schemas/create-publication-upload-url.core-gql';
import { schema as removeAttributeSchema } from '../schemas/remove-attribute.core-gql';
import { schema as listUserPublications } from '../schemas/list-user-publications.core-gql';
import { schema as createPublicationSchema } from '../schemas/create-publication.core-gql';
import { schema as tagResourceSchema } from '../schemas/tag-resource.core-gql';
import { from, lastValueFrom, mergeMap, toArray } from 'rxjs';
import { parseISO } from 'date-fns';
import { useRouter } from 'next/router';
import _ from 'lodash';

export type UseLastKnownPublicationStepOutput = {
  update: (step: StepName) => void;
};

export function useLastKnownPublicationStep(
  publicationId: string
): UseLastKnownPublicationStepOutput {
  const loggedInUser = useLoggedInUser();

  const [updateStep] = useMutation<
    GqlTagResourceMutation,
    GqlTagResourceMutationVariables
  >(tagResourceSchema);

  return {
    update: async (step) => {
      const arn = `arn:core:PropertyListingPublication:${publicationId}`;
      await updateStep({
        variables: {
          arn,
          tags: [
            {
              key: `${loggedInUser?.data?.id}/last_known_step`,
              value: step,
            },
          ],
        },
      });
    },
  };
}

export type UserPublication = {
  id: string;
  status: {
    type: string;
    since: string;
  };
  authors: {
    id: number;
  }[];
  name: string | undefined;
  address: string | undefined;
  description: string;
  lastKnownStep: StepName | null;
  createdAt: Date;
  updatedAt: Date;
};

function parseUserPublicationData(
  data: GqlListUserPublicationsQuery
): UserPublication[] {
  return data.listUserPropertyListingPublications.publications.map((item) => {
    const lastKnownStep = StepName.nullable().parse(
      item.lastKnownStep?.value ?? null
    );

    return {
      id: item.id,
      status: {
        type: item.status.type,
        since: item.status.since,
      },
      authors: item.authors,
      name: item.name?.value,
      address: item.plainAddress?.plainAddress?.value,
      description: item.description?.value ?? '',
      createdAt: parseISO(item.createdAt),
      updatedAt: parseISO(item.updatedAt),
      lastKnownStep: lastKnownStep,
    };
  });
}

export type UseUserPublicationsOutput = {
  loading: boolean;
  error: ApolloError | undefined;
  data: UserPublication[];
};

export function useUserPublications(): UseUserPublicationsOutput {
  const user = useLoggedInUser();
  const userId = user?.data?.id;

  const { loading, data, error } = useQuery<
    GqlListUserPublicationsQuery,
    GqlListUserPublicationsQueryVariables
  >(listUserPublications, {
    errorPolicy: 'all',
    context: { clientName: AptunoApolloLink.CORE },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    variables: {
      nameKey: `${userId}/publication-name`,
      descriptionKey: `${userId}/publication-description`,
      lastKnownStepKey: `${userId}/last_known_step`,
    },
  });

  return {
    loading: loading,
    error: error,
    data: data ? parseUserPublicationData(data) : [],
  };
}

export type PublicationPreQualification = {
  estateType?: 'apartment' | 'house';
  estateUsage?: 'commercial' | 'residential';
  userIntention?: 'sell' | 'rent';
};

namespace PublicationPreQualification {
  export const EMPTY = {
    estateType: undefined,
    estateUsage: undefined,
    userIntention: undefined,
  };
}

export type PublicationPreQualificationUpdateArgs = {
  estateType: 'apartment' | 'house';
  estateUsage: 'commercial' | 'residential';
  userIntention: 'sell' | 'rent';
};

export type UsePublicationPreQualificationOutput = {
  loading: boolean;
  error: ApolloError | undefined;
  data: PublicationPreQualification;
  update: (data: PublicationPreQualificationUpdateArgs) => Promise<void>;
};

function parsePreQualificationData(
  data: GqlGetPublicationPreQualificationQuery
): PublicationPreQualification {
  if (
    data.getPropertyListingPublication.__typename ===
    'GetPropertyListingPublicationFailure'
  ) {
    return PublicationPreQualification.EMPTY;
  }

  const publication = data.getPropertyListingPublication.publication;

  return {
    estateType: publication.estateType?.estateType?.value as
      | 'apartment'
      | 'house'
      | undefined,
    estateUsage: publication.estateUsage?.estateUsage?.value as
      | 'commercial'
      | 'residential'
      | undefined,
    userIntention: publication.user_intention?.userIntention?.value as
      | 'sell'
      | 'rent'
      | undefined,
  };
}

export type UsePublicationPreQualificationProps = {
  onError?: (error: ApolloError | Error | GqlGqlError) => void;
  onSuccess?: (data: PublicationPreQualification) => void;
};

export function usePublicationPreQualification(
  props: UsePublicationPreQualificationProps
): UsePublicationPreQualificationOutput {
  const publicationId = usePublicationId();
  const { loading, data, error } = useQuery<
    GqlGetPublicationPreQualificationQuery,
    GqlGetPublicationPreQualificationQueryVariables
  >(getPreQualificationSchema, {
    errorPolicy: 'all',
    context: { clientName: AptunoApolloLink.CORE },
    variables: { publicationId },
    onError: (error) => {
      props.onError?.(error);
    },
    onCompleted: (data) => {
      switch (data.getPropertyListingPublication.__typename) {
        case 'GetPropertyListingPublicationFailure':
          props.onError?.(data.getPropertyListingPublication.error);
          break;
        case 'GetPropertyListingPublicationSuccess':
          const parsedData = parsePreQualificationData(data);
          props.onSuccess?.(parsedData);
          break;
      }
    },
  });

  const [updatePublicationPreQualification, { loading: updateLoading }] =
    useMutation<
      GqlUpdatePublicationAttributesMutation,
      GqlUpdatePublicationAttributesMutationVariables
    >(updatePublicationAttributesSchema, {
      context: { clientName: AptunoApolloLink.CORE },
      refetchQueries: [
        {
          query: getPreQualificationSchema,
          variables: { publicationId },
        },
      ],
    });

  return {
    loading: loading || updateLoading,
    error: error,
    data: data
      ? parsePreQualificationData(data)
      : PublicationPreQualification.EMPTY,
    update: async (data) => {
      const items: GqlPropertyListingPublicationAttributeInput[] = [
        {
          type: PropertyListingPublicationAttributeInputType.PRE_QUALIFICATION_ESTATE_TYPE,
          estateType: { value: data.estateType },
        },
        {
          type: PropertyListingPublicationAttributeInputType.PRE_QUALIFICATION_ESTATE_USAGE,
          estateUsage: { value: data.estateUsage },
        },
        {
          type: PropertyListingPublicationAttributeInputType.PRE_QUALIFICATION_USER_INTENTION,
          userIntention: { value: data.userIntention },
        },
      ];

      const variables: GqlUpdatePublicationAttributesMutationVariables = {
        publicationId,
        items,
      };

      await updatePublicationPreQualification({
        variables,
      });
    },
  };
}

export type PublicationBasicInformation = {
  bathrooms?: number;
  bedrooms?: number;
  areaM2?: number;
  parkingSpaces?: number;
  floor?: number;
  stratum?: number;
  petFriendly?: boolean;
  constructionYear?: number;
  furnished?: boolean;
};

namespace PublicationBasicInformation {
  export const EMPTY = {
    bathrooms: undefined,
    bedrooms: undefined,
    areaM2: undefined,
    parkingSpaces: undefined,
    floor: undefined,
    stratum: undefined,
    petFriendly: undefined,
    constructionYear: undefined,
    furnished: undefined,
  };
}

export type PublicationBasicInformationUpdateArgs = {
  bathrooms: number;
  bedrooms: number;
  areaM2: number;
  parkingSpaces: number;
  floor: number;
  stratum: number;
  petFriendly: boolean;
  constructionYear: number;
  furnished: boolean;
};

export type UsePublicationBasicInformationInput = {
  onError?: (error: GqlGqlError | Error) => void;
  onSuccess?: (data: PublicationBasicInformation) => void;
};
export type UsePublicationBasicInformationOutput = {
  loading: boolean;
  data: PublicationBasicInformation;
  error: {
    get: GqlGqlError | Error | null;
    update: GqlGqlError | Error | null;
  };
  update: (data: PublicationBasicInformationUpdateArgs) => Promise<void>;
};

function parseBasicInformationData(
  data: GqlGetPublicationBasicInformationQuery
): PublicationBasicInformation {
  if (
    data.getPropertyListingPublication.__typename ===
    'GetPropertyListingPublicationFailure'
  ) {
    return PublicationBasicInformation.EMPTY;
  }

  const publication = data.getPropertyListingPublication.publication;

  return {
    bathrooms: publication.bathrooms?.bathrooms?.value,
    bedrooms: publication.bedrooms?.bedrooms?.value,
    areaM2: publication.areaM2?.areaM2?.value,
    parkingSpaces: publication.parkingSpaces?.parkingSpaces?.value,
    floor: publication.floor?.floor?.value,
    stratum: publication.stratum?.stratum?.value,
    petFriendly: publication.petFriendly?.petFriendly?.value,
    constructionYear: publication.constructionYear?.constructionYear?.value,
    furnished: publication.furnished?.furnished?.value,
  };
}

export function usePublicationBasicInformation(
  input: UsePublicationBasicInformationInput
): UsePublicationBasicInformationOutput {
  const { onError, onSuccess } = input;

  const publicationId = usePublicationId();

  const {
    loading: getLoading,
    data: getData,
    error: getError,
  } = useQuery<
    GqlGetPublicationBasicInformationQuery,
    GqlGetPublicationBasicInformationQueryVariables
  >(getBasicInformationSchema, {
    errorPolicy: 'all',
    context: { clientName: AptunoApolloLink.CORE },
    variables: { publicationId },
    onError: (error) => {
      onError?.(error);
    },
    onCompleted: (data) => {
      switch (data.getPropertyListingPublication.__typename) {
        case 'GetPropertyListingPublicationFailure':
          onError?.(data.getPropertyListingPublication.error);
          break;
        case 'GetPropertyListingPublicationSuccess':
          const parsedData = parseBasicInformationData(data);
          onSuccess?.(parsedData);
          break;
      }
    },
  });

  const [
    updatePublicationBasicInformation,
    { loading: updateLoading, error: updateError },
  ] = useMutation<
    GqlUpdatePublicationAttributesMutation,
    GqlUpdatePublicationAttributesMutationVariables
  >(updatePublicationAttributesSchema, {
    context: { clientName: AptunoApolloLink.CORE },
    errorPolicy: 'all',
    refetchQueries: [
      {
        query: getBasicInformationSchema,
        variables: { publicationId },
      },
    ],
  });

  return {
    loading: getLoading || updateLoading,
    error: {
      get:
        getData?.getPropertyListingPublication?.__typename ===
        'GetPropertyListingPublicationFailure'
          ? getData.getPropertyListingPublication.error
          : getError || null,
      update: updateError || null,
    },
    data:
      getData?.getPropertyListingPublication.__typename ===
      'GetPropertyListingPublicationSuccess'
        ? parseBasicInformationData(getData)
        : PublicationBasicInformation.EMPTY,
    update: async (information) => {
      const items: GqlPropertyListingPublicationAttributeInput[] = [
        {
          type: PropertyListingPublicationAttributeInputType.BASIC_INFORMATION_BATHROOMS,
          bathrooms: { value: information.bathrooms },
        },
        {
          type: PropertyListingPublicationAttributeInputType.BASIC_INFORMATION_BEDROOMS,
          bedrooms: { value: information.bedrooms },
        },
        {
          type: PropertyListingPublicationAttributeInputType.BASIC_INFORMATION_AREA_M2,
          areaM2: { value: information.areaM2 },
        },
        {
          type: PropertyListingPublicationAttributeInputType.BASIC_INFORMATION_PARKING_SPACES,
          parkingSpaces: { value: information.parkingSpaces },
        },
        {
          type: PropertyListingPublicationAttributeInputType.BASIC_INFORMATION_FLOOR,
          floor: { value: information.floor },
        },
        {
          type: PropertyListingPublicationAttributeInputType.BASIC_INFORMATION_STRATUM,
          stratum: { value: information.stratum },
        },
        {
          type: PropertyListingPublicationAttributeInputType.BASIC_INFORMATION_PET_FRIENDLY,
          petFriendly: { value: information.petFriendly },
        },
        {
          type: PropertyListingPublicationAttributeInputType.BASIC_INFORMATION_CONSTRUCTION_YEAR,
          constructionYear: { value: information.constructionYear },
        },
        {
          type: PropertyListingPublicationAttributeInputType.BASIC_INFORMATION_FURNISHED,
          furnished: { value: information.furnished },
        },
      ];

      const variables: GqlUpdatePublicationAttributesMutationVariables = {
        publicationId,
        items,
      };

      await updatePublicationBasicInformation({
        variables,
      });
    },
  };
}

export type PublicationLocation = {
  address?: {
    value: string;
    details: string;
  };
  location?: {
    latitude: number;
    longitude: number;
  };
};

namespace PublicationLocation {
  export const EMPTY = {
    address: undefined,
    location: undefined,
  };
}

export type PublicationLocationUpdateArgs = {
  address: {
    value: string;
    details: string;
  };
  location: {
    latitude: number;
    longitude: number;
  };
};

function parseLocationData(
  data: GqlGetPublicationLocationQuery
): PublicationLocation {
  if (
    data.getPropertyListingPublication.__typename ===
    'GetPropertyListingPublicationFailure'
  ) {
    return PublicationLocation.EMPTY;
  }

  const publication = data.getPropertyListingPublication.publication;

  return {
    address: {
      value: publication.plainAddress?.plainAddress?.value ?? '',
      details: publication.plainAddress?.plainAddress?.details ?? '',
    },
    location: publication.location?.location
      ? publication.location.location
      : undefined,
  };
}

export type UsePublicationLocationInput = {
  onError?: (error: GqlGqlError | Error) => void;
  onSuccess?: (data: PublicationLocation) => void;
};

export type UsePublicationLocationOutput = {
  loading: boolean;
  data: PublicationLocation;
  error: GqlGqlError | Error | null;
  update: (data: PublicationLocationUpdateArgs) => Promise<void>;
};

export function usePublicationLocation(
  input: UsePublicationLocationInput
): UsePublicationLocationOutput {
  const { onError, onSuccess } = input;

  const publicationId = usePublicationId();

  const {
    loading: getLoading,
    data: getData,
    error: getError,
  } = useQuery<
    GqlGetPublicationLocationQuery,
    GqlGetPublicationLocationQueryVariables
  >(getLocationSchema, {
    errorPolicy: 'all',
    context: { clientName: AptunoApolloLink.CORE },
    variables: { publicationId },
    onError: (error) => {
      onError?.(error);
    },
    onCompleted: (data) => {
      switch (data.getPropertyListingPublication.__typename) {
        case 'GetPropertyListingPublicationFailure':
          onError?.(data.getPropertyListingPublication.error);
          break;
        case 'GetPropertyListingPublicationSuccess':
          const parsedData = parseLocationData(data);
          onSuccess?.(parsedData);
          break;
      }
    },
  });

  const [
    updatePublicationLocation,
    { loading: updateLoading, error: updateError },
  ] = useMutation<
    GqlUpdatePublicationAttributesMutation,
    GqlUpdatePublicationAttributesMutationVariables
  >(updatePublicationAttributesSchema, {
    context: { clientName: AptunoApolloLink.CORE },
    refetchQueries: [
      {
        query: getLocationSchema,
        variables: { publicationId },
      },
    ],
  });

  return {
    loading: getLoading || updateLoading,
    error: getError || updateError || null,
    data:
      getData?.getPropertyListingPublication.__typename ===
      'GetPropertyListingPublicationSuccess'
        ? parseLocationData(getData)
        : PublicationLocation.EMPTY,
    update: async (updateArgs) => {
      const items: GqlPropertyListingPublicationAttributeInput[] = [
        {
          type: PropertyListingPublicationAttributeInputType.BASIC_INFORMATION_PLAIN_ADDRESS,
          plainAddress: {
            value: updateArgs.address.value,
            details: updateArgs.address.details,
          },
        },
        {
          type: PropertyListingPublicationAttributeInputType.BASIC_INFORMATION_LOCATION,
          location: {
            latitude: updateArgs.location.latitude,
            longitude: updateArgs.location.longitude,
          },
        },
      ];

      const variables: GqlUpdatePublicationAttributesMutationVariables = {
        publicationId,
        items,
      };

      await updatePublicationLocation({
        variables,
      });
    },
  };
}

export type PublicationExtraInformation = {
  complementaryAreas?: {
    balcony: boolean;
    storageRoom: boolean;
    patio: boolean;
    terrace: boolean;
  };
  security?: {
    alarm: boolean;
    cctv: boolean;
    securityDoor: boolean;
  };
  interiorFeatures?: {
    heating: boolean;
    fireplace: boolean;
    serviceRoom: boolean;
    studyLibrary: boolean;
    gasInstallation: boolean;
    jacuzzi: boolean;
  };
  exteriorCharacteristics?: {
    elevator: boolean;
    basketballCourt: boolean;
    soccerField: boolean;
    squashCourt: boolean;
    tennisCourt: boolean;
    gym: boolean;
    pool: boolean;
    communalRoom: boolean;
    sauna: boolean;
    bbqArea: boolean;
    childrenArea: boolean;
    greenAreas: boolean;
  };
};

namespace PublicationExtraInformation {
  export const EMPTY = {
    complementaryAreas: {
      balcony: false,
      storageRoom: false,
      patio: false,
      terrace: false,
    },
    security: {
      alarm: false,
      cctv: false,
      securityDoor: false,
    },
    interiorFeatures: {
      heating: false,
      fireplace: false,
      serviceRoom: false,
      studyLibrary: false,
      gasInstallation: false,
      jacuzzi: false,
    },
    exteriorCharacteristics: {
      elevator: false,
      basketballCourt: false,
      soccerField: false,
      squashCourt: false,
      tennisCourt: false,
      gym: false,
      pool: false,
      communalRoom: false,
      sauna: false,
      bbqArea: false,
      childrenArea: false,
      greenAreas: false,
    },
  };
}

export type PublicationExtraInformationUpdateArgs = {
  complementaryAreas: {
    balcony: boolean;
    storageRoom: boolean;
    patio: boolean;
    terrace: boolean;
  };
  security: {
    alarm: boolean;
    cctv: boolean;
    securityDoor: boolean;
  };
  interiorFeatures: {
    heating: boolean;
    fireplace: boolean;
    serviceRoom: boolean;
    studyLibrary: boolean;
    gasInstallation: boolean;
    jacuzzi: boolean;
  };
  exteriorCharacteristics: {
    elevator: boolean;
    basketballCourt: boolean;
    soccerField: boolean;
    squashCourt: boolean;
    tennisCourt: boolean;
    gym: boolean;
    pool: boolean;
    communalRoom: boolean;
    sauna: boolean;
    bbqArea: boolean;
    childrenArea: boolean;
    greenAreas: boolean;
  };
};

export type UsePublicationExtraInformationInput = {
  onError?: (error: GqlGqlError | Error) => void;
  onSuccess?: (data: PublicationExtraInformation) => void;
};

export type UsePublicationExtraInformationOutput = {
  loading: boolean;
  data: PublicationExtraInformation;
  error: GqlGqlError | Error | null;
  update: (data: PublicationExtraInformationUpdateArgs) => Promise<void>;
};

function parseExtraInformationData(
  data: GqlGetPublicationFeaturesQuery
): PublicationExtraInformation {
  if (
    data.getPropertyListingPublication.__typename ===
    'GetPropertyListingPublicationFailure'
  ) {
    return PublicationExtraInformation.EMPTY;
  }

  const publication = data.getPropertyListingPublication.publication;

  return {
    complementaryAreas: {
      balcony:
        publication.complementaryAreas?.complementaryAreas?.balcony ?? false,
      storageRoom:
        publication.complementaryAreas?.complementaryAreas?.storageRoom ??
        false,
      patio: publication.complementaryAreas?.complementaryAreas?.patio ?? false,
      terrace:
        publication.complementaryAreas?.complementaryAreas?.terrace ?? false,
    },
    security: {
      alarm: publication.security?.security?.alarm ?? false,
      cctv: publication.security?.security?.cctv ?? false,
      securityDoor: publication.security?.security?.securityDoor ?? false,
    },
    interiorFeatures: {
      heating: publication.interiorFeatures?.interiorFeatures?.heating ?? false,
      fireplace:
        publication.interiorFeatures?.interiorFeatures?.fireplace ?? false,
      serviceRoom:
        publication.interiorFeatures?.interiorFeatures?.serviceRoom ?? false,
      studyLibrary:
        publication.interiorFeatures?.interiorFeatures?.studyLibrary ?? false,
      gasInstallation:
        publication.interiorFeatures?.interiorFeatures?.gasInstallation ??
        false,
      jacuzzi: publication.interiorFeatures?.interiorFeatures?.jacuzzi ?? false,
    },
    exteriorCharacteristics: {
      elevator:
        publication.exteriorCharacteristics?.exteriorCharacteristics
          ?.elevator ?? false,
      basketballCourt:
        publication.exteriorCharacteristics?.exteriorCharacteristics
          ?.basketballCourt ?? false,
      soccerField:
        publication.exteriorCharacteristics?.exteriorCharacteristics
          ?.soccerField ?? false,
      squashCourt:
        publication.exteriorCharacteristics?.exteriorCharacteristics
          ?.squashCourt ?? false,
      tennisCourt:
        publication.exteriorCharacteristics?.exteriorCharacteristics
          ?.tennisCourt ?? false,
      gym:
        publication.exteriorCharacteristics?.exteriorCharacteristics?.gym ??
        false,
      pool:
        publication.exteriorCharacteristics?.exteriorCharacteristics?.pool ??
        false,
      communalRoom:
        publication.exteriorCharacteristics?.exteriorCharacteristics
          ?.communalRoom ?? false,
      sauna:
        publication.exteriorCharacteristics?.exteriorCharacteristics?.sauna ??
        false,
      bbqArea:
        publication.exteriorCharacteristics?.exteriorCharacteristics?.bbqArea ??
        false,
      childrenArea:
        publication.exteriorCharacteristics?.exteriorCharacteristics
          ?.childrenArea ?? false,
      greenAreas:
        publication.exteriorCharacteristics?.exteriorCharacteristics
          ?.greenAreas ?? false,
    },
  };
}

export function usePublicationExtraInformation(
  input: UsePublicationExtraInformationInput
): UsePublicationExtraInformationOutput {
  const { onError, onSuccess } = input;

  const publicationId = usePublicationId();

  const {
    loading: getLoading,
    data: getData,
    error: getError,
  } = useQuery<
    GqlGetPublicationFeaturesQuery,
    GqlGetPublicationLocationQueryVariables
  >(getFeaturesSchema, {
    errorPolicy: 'all',
    context: { clientName: AptunoApolloLink.CORE },
    variables: { publicationId },
    onError: (error) => {
      onError?.(error);
    },
    onCompleted: (data) => {
      switch (data.getPropertyListingPublication.__typename) {
        case 'GetPropertyListingPublicationFailure':
          onError?.(data.getPropertyListingPublication.error);
          break;
        case 'GetPropertyListingPublicationSuccess':
          const parsedData = parseExtraInformationData(data);
          onSuccess?.(parsedData);
          break;
      }
    },
  });

  const [
    updatePublicationExtraInformation,
    { loading: updateLoading, error: updateError },
  ] = useMutation<
    GqlUpdatePublicationAttributesMutation,
    GqlUpdatePublicationAttributesMutationVariables
  >(updatePublicationAttributesSchema, {
    context: { clientName: AptunoApolloLink.CORE },
    refetchQueries: [
      {
        query: getFeaturesSchema,
        variables: { publicationId },
      },
    ],
  });

  return {
    loading: getLoading || updateLoading,
    error: getError || updateError || null,
    data:
      getData?.getPropertyListingPublication.__typename ===
      'GetPropertyListingPublicationSuccess'
        ? parseExtraInformationData(getData)
        : PublicationExtraInformation.EMPTY,
    update: async (information) => {
      const items: GqlPropertyListingPublicationAttributeInput[] = [
        {
          type: PropertyListingPublicationAttributeInputType.AMENITIES_COMPLEMENTARY_AREAS,
          complementaryAreas: information.complementaryAreas,
        },
        {
          type: PropertyListingPublicationAttributeInputType.AMENITIES_SECURITY,
          security: information.security,
        },
        {
          type: PropertyListingPublicationAttributeInputType.AMENITIES_INTERIOR_FEATURES,
          interiorFeatures: information.interiorFeatures,
        },
        {
          type: PropertyListingPublicationAttributeInputType.AMENITIES_EXTERIOR_CHARACTERISTICS,
          exteriorCharacteristics: information.exteriorCharacteristics,
        },
      ];

      const variables: GqlUpdatePublicationAttributesMutationVariables = {
        publicationId,
        items,
      };

      await updatePublicationExtraInformation({
        variables,
      });
    },
  };
}

export type PublicationPrices = {
  rentPrice?: number;
  administrationPrice?: number;
};

namespace PublicationPrices {
  export const EMPTY = {
    rentPrice: undefined,
    administrationPrice: undefined,
  };
}

export type PublicationPricesUpdateArgs = {
  rentPrice: number;
  administrationPrice: number;
};

export type UsePublicationPricesInput = {
  onError?: (error: GqlGqlError | Error) => void;
  onSuccess?: (data: PublicationPrices) => void;
};

export type UsePublicationPricesOutput = {
  loading: boolean;
  data: PublicationPrices;
  error: GqlGqlError | Error | null;
  update: (data: PublicationPricesUpdateArgs) => Promise<void>;
};

function parsePricesData(
  data: GqlGetPublicationPricesQuery
): PublicationPrices {
  if (
    data.getPropertyListingPublication.__typename ===
    'GetPropertyListingPublicationFailure'
  ) {
    return PublicationPrices.EMPTY;
  }

  const publication = data.getPropertyListingPublication.publication;

  return {
    rentPrice: publication.rent?.rentPrice?.value,
    administrationPrice: publication.administration?.administrationPrice?.value,
  };
}

export function usePublicationPrices(
  input: UsePublicationPricesInput
): UsePublicationPricesOutput {
  const { onError, onSuccess } = input;

  const publicationId = usePublicationId();

  const {
    loading: getLoading,
    data: getData,
    error: getError,
  } = useQuery<
    GqlGetPublicationPricesQuery,
    GqlGetPublicationPricesQueryVariables
  >(getPricesSchema, {
    errorPolicy: 'all',
    context: { clientName: AptunoApolloLink.CORE },
    variables: { publicationId },
    onError: (error) => {
      onError?.(error);
    },
    onCompleted: (data) => {
      switch (data.getPropertyListingPublication.__typename) {
        case 'GetPropertyListingPublicationFailure':
          onError?.(data.getPropertyListingPublication.error);
          break;
        case 'GetPropertyListingPublicationSuccess':
          const parsedData = parsePricesData(data);
          onSuccess?.(parsedData);
          break;
      }
    },
  });

  const [
    updatePublicationPrices,
    { loading: updateLoading, error: updateError },
  ] = useMutation<
    GqlUpdatePublicationAttributesMutation,
    GqlUpdatePublicationAttributesMutationVariables
  >(updatePublicationAttributesSchema, {
    context: { clientName: AptunoApolloLink.CORE },
    refetchQueries: [
      {
        query: getPricesSchema,
        variables: { publicationId },
      },
    ],
  });

  return {
    loading: getLoading || updateLoading,
    error: getError || updateError || null,
    data:
      getData?.getPropertyListingPublication.__typename ===
      'GetPropertyListingPublicationSuccess'
        ? parsePricesData(getData)
        : PublicationPrices.EMPTY,
    update: async (prices) => {
      const items: GqlPropertyListingPublicationAttributeInput[] = [
        {
          type: PropertyListingPublicationAttributeInputType.PRICE_RENT,
          rentPrice: {
            currency: 'COP',
            value: prices.rentPrice,
          },
        },
        {
          type: PropertyListingPublicationAttributeInputType.PRICE_ADMINISTRATION,
          administrationPrice: {
            currency: 'COP',
            value: prices.administrationPrice,
          },
        },
      ];

      const variables: GqlUpdatePublicationAttributesMutationVariables = {
        publicationId,
        items,
      };

      await updatePublicationPrices({
        variables,
      });
    },
  };
}

export type MediaImage = {
  attachmentId: string;
  url: string;
  fileName: string;
};

export type MoveImageInput = {
  direction: 'up' | 'down';
  index: number;
  attachmentId: string;
};

export type UsePublicationMediaImagesOutput = {
  loading: boolean;
  error: {
    get: GqlGqlError | Error | null;
    update: GqlGqlError | Error | null;
  };
  data: MediaImage[];
  delete: (attachmentId: string) => Promise<void>;
  upload: (files: File[]) => Promise<unknown>;
  move: (input: MoveImageInput) => Promise<void>;
};

function parseAndOrderImagesData(
  data: GqlGetPublicationImagesQuery | undefined
): MediaImage[] {
  if (
    !data ||
    data.getPropertyListingPublication.__typename ===
      'GetPropertyListingPublicationFailure'
  ) {
    return [];
  }

  const publication = data.getPropertyListingPublication.publication;

  const images = publication.images.flatMap((image) => {
    if (!image) {
      return [];
    }

    const item = {
      attachmentId: image.attachmentId,
      url: image.url,
      fileName: image.fileName?.value ?? '',
    };

    return [item];
  });

  const safeOrder = _.get(
    publication.images_order,
    'mediaImagesOrder.order',
    []
  );

  const orderMap = safeOrder.reduce(
    (acc, current, index) => ({ ...acc, [current]: index }),
    {}
  );

  const imagesWithOrder = images.map((image, index) => {
    const order = _.get(orderMap, image.attachmentId, index);

    return {
      ...image,
      order,
    };
  });

  return _.orderBy(imagesWithOrder, ['order', 'attachmentId'], ['asc', 'asc']);
}

export function usePublicationMediaImages(): UsePublicationMediaImagesOutput {
  const publicationId = usePublicationId();

  const {
    data,
    loading: getLoading,
    error,
    refetch: refetchMediaImages,
  } = useQuery<
    GqlGetPublicationImagesQuery,
    GqlGetPublicationImagesQueryVariables
  >(getPublicationImagesSchema, {
    context: { clientName: AptunoApolloLink.CORE },
    variables: { publicationId },
  });

  const [updateAttributes, { loading: updateLoading, error: updateError }] =
    useMutation<
      GqlUpdatePublicationAttributesMutation,
      GqlUpdatePublicationAttributesMutationVariables
    >(updatePublicationAttributesSchema, {
      context: { clientName: AptunoApolloLink.CORE },
    });

  const [createUploadUrl, { loading: createUploadUrlLoading }] = useMutation<
    GqlCreatePropertyListingPublicationUploadUrlMutation,
    GqlCreatePropertyListingPublicationUploadUrlMutationVariables
  >(createUploadUrlSchema, {
    context: { clientName: AptunoApolloLink.CORE },
  });

  const [removeAttribute, { loading: removeAttributeLoading }] = useMutation<
    GqlRemovePropertyListingPublicationAttributeMutation,
    GqlRemovePropertyListingPublicationAttributeMutationVariables
  >(removeAttributeSchema, {
    context: { clientName: AptunoApolloLink.CORE },
  });

  const [uploading, setUploading] = useState(false);

  const uploadImages = async (files: File[]) => {
    if (files.length === 0) {
      return;
    }

    const operations = from(files).pipe(
      mergeMap(async (file) => {
        const result = await createUploadUrl({
          variables: {
            publicationId,
            fileName: file.name,
          },
        });

        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        if (!result.data) {
          throw new Error('No data');
        }

        switch (
          result.data.createPropertyListingPublicationUploadUrl.__typename
        ) {
          case 'CreatePropertyListingPublicationUploadMediaUrlFailure': {
            throw new Error(
              result.data.createPropertyListingPublicationUploadUrl.error.message
            );
          }
          case 'CreatePropertyListingPublicationUploadMediaUrlSuccess': {
            const uploadUrl =
              result.data.createPropertyListingPublicationUploadUrl.uploadUrl;

            const response = await fetch(uploadUrl, {
              method: 'PUT',
              body: file,
            });

            if (!response.ok) {
              throw new Error('Error uploading file');
            }

            return {
              attachmentId:
                result.data.createPropertyListingPublicationUploadUrl
                  .attachmentId,
              url: uploadUrl,
              fileName: file.name,
            };
          }
        }
      }),
      toArray(),
      mergeMap((items) => {
        const mappedItems: GqlPropertyListingPublicationAttributeInput[] =
          items.map((item) => ({
            type: PropertyListingPublicationAttributeInputType.MEDIA_IMAGE,
            mediaImage: {
              attachmentId: item.attachmentId,
            },
          }));

        return updateAttributes({
          variables: {
            publicationId,
            items: mappedItems,
          },
        });
      })
    );

    return lastValueFrom(operations);
  };

  const orderedImages = parseAndOrderImagesData(data);
  return {
    loading:
      getLoading ||
      updateLoading ||
      createUploadUrlLoading ||
      removeAttributeLoading ||
      uploading,
    error: {
      get: error || null,
      update: updateError || null,
    },
    data: orderedImages,
    delete: async (attachmentId) => {
      await removeAttribute({
        variables: {
          publicationId,
          attributeId: `media/image/${attachmentId}`,
        },
      });

      await new Promise((resolve) => setTimeout(resolve, 375));

      await refetchMediaImages();
    },
    upload: async (files) => {
      setUploading(true);
      try {
        await uploadImages(files);
        await new Promise((resolve) => setTimeout(resolve, 375));
        await refetchMediaImages();
      } finally {
        setUploading(false);
      }
    },
    move: async (input) => {
      const { direction, index } = input;

      const newOrder = [...orderedImages].map((item) => item.attachmentId);

      const isFirst = index === 0;
      const isLast = index === newOrder.length - 1;
      const moveFirstImageUp = direction === 'up' && isFirst;
      const moveLastImageDown = direction === 'down' && isLast;

      if (moveFirstImageUp || moveLastImageDown) {
        return; // Can't move further in this direction
      }

      const swapIndex = direction === 'up' ? index - 1 : index + 1;

      const temp = newOrder[index];
      newOrder[index] = newOrder[swapIndex];
      newOrder[swapIndex] = temp;

      const itemsToUpdate = [
        {
          type: PropertyListingPublicationAttributeInputType.MEDIA_IMAGES_ORDER,
          mediaImagesOrder: {
            order: newOrder,
          },
        },
      ];

      await updateAttributes({
        variables: {
          publicationId,
          items: itemsToUpdate,
        },
        refetchQueries: [
          {
            query: getPublicationImagesSchema,
            variables: { publicationId },
          },
        ],
      });
    },
  };
}

export type CreatePublicationArgs = {
  name: string;
  description: string;
};

export type UseCreatePublicationOutput = {
  loading: boolean;
  error: GqlGqlError | ApolloError | undefined;
  create: (data: CreatePublicationArgs) => Promise<string>;
};

export function useCreatePublication(): UseCreatePublicationOutput {
  const user = useLoggedInUser();
  const router = useRouter();

  const [createPublication, { loading, error }] = useMutation<
    GqlCreatePropertyListingPublicationMutation,
    GqlCreatePropertyListingPublicationMutationVariables
  >(createPublicationSchema, {
    context: { clientName: AptunoApolloLink.CORE },
    refetchQueries: [
      {
        query: listUserPublications,
        variables: {
          nameKey: `${user?.data?.id}/publication-name`,
          descriptionKey: `${user?.data?.id}/publication-description`,
          lastKnownStepKey: `${user?.data?.id}/last_known_step`,
        },
      },
    ],
  });

  return {
    loading,
    error,
    create: async (data) => {
      const descriptionTag =
        data.description.trim() !== ''
          ? [
              {
                key: `${user?.data?.id}/publication-description`,
                value: data.description,
              },
            ]
          : [];

      const queryParamsAsTags = new URLSearchParams(
        router.query as Record<string, string>
      ).entries();

      const tags = Array.from(queryParamsAsTags).map(([key, value]) => ({
        key: key,
        value: String(value),
      }));

      const result = await createPublication({
        variables: {
          tags: [
            {
              key: `${user?.data?.id}/publication-name`,
              value: data.name,
            },
            ...descriptionTag,
            ...tags,
          ],
        },
      });

      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      if (!result.data) {
        throw new Error('No data received from the server');
      }

      switch (result.data.createPropertyListingPublication.__typename) {
        case 'CreatePropertyListingPublicationFailure':
          throw new Error(
            result.data.createPropertyListingPublication.error.message
          );
        case 'CreatePropertyListingPublicationSuccess':
          return result.data.createPropertyListingPublication.publication.id;
      }
    },
  };
}
